import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactSelect from 'components/base/ReactSelect';
import IMerchCategory from 'models/restful/IMerchCategory';
import { useEffect, useRef, useState } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  ClearIndicatorProps,
  components,
  MultiValue,
  MultiValueProps,
  ValueContainerProps
} from 'react-select';
import makeAnimated from 'react-select/animated';

const animatedComponents = makeAnimated();

interface CategoryDropdownProps {
  title: string;
  categories: IMerchCategory[];
  categoryId: number;
  isFiltered: boolean;

  onCategoryChange: (
    categoryId: number,
    selectedCategories: IMerchCategory[]
  ) => void;
}

const ClearIndicator = (props: ClearIndicatorProps) => (
  <components.ClearIndicator {...props} className="py-0 cursor-pointer">
    <FontAwesomeIcon icon={faXmark} className="text-900" />
  </components.ClearIndicator>
);

function CategoryDropdown({
  title,
  categories,
  categoryId,
  isFiltered,
  onCategoryChange
}: CategoryDropdownProps) {
  const [selected, setSelected] = useState<MultiValue<IMerchCategory>>([]);
  const [dropdownKey, setDropdownKey] = useState(crypto.randomUUID());
  const tooltipId = useRef(`hidden-items-${crypto.randomUUID()}`);
  const [maxVisibleItems, setMaxVisibleItems] = useState(3);

  const [calculatedVisibleCount, setCalculatedVisibleCount] =
    useState(maxVisibleItems);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFiltered) {
      setSelected([]);
      setDropdownKey(crypto.randomUUID());
    }
  }, [isFiltered]);
  useEffect(() => {
    setSelected(categories.filter(cat => selected.some(c => c.ID === cat.ID)));
  }, [categories]);

  useEffect(() => {
    let visibleCount = maxVisibleItems;
    if (selected.length <= maxVisibleItems) {
      visibleCount = selected.length;
    }
    setCalculatedVisibleCount(visibleCount);
  }, [selected, maxVisibleItems]);

  const handleChange = (newValue: unknown) => {
    const typedValue = newValue as MultiValue<IMerchCategory>;
    setSelected(typedValue);

    const selectedCategories = typedValue.map(cat => cat as IMerchCategory);
    onCategoryChange(categoryId, selectedCategories);
  };

  const options = categories.map(cat => ({
    value: cat.ID,
    label: cat.Title,
    ...cat
  }));

  useEffect(() => {
    const updateItemsPerRow = () => {
      const width = window.innerWidth;

      if (width >= 1920) {
        setMaxVisibleItems(3);
      } else if (width >= 1400) {
        setMaxVisibleItems(2);
      } else if (width >= 600) {
        setMaxVisibleItems(1);
      } else {
        setMaxVisibleItems(3);
      }
    };

    updateItemsPerRow(); // Запускаем при загрузке
    window.addEventListener('resize', updateItemsPerRow);

    return () => {
      window.removeEventListener('resize', updateItemsPerRow);
    };
  }, []);

  const CustomMultiValue = (props: MultiValueProps) => {
    const { index } = props;
    if (index >= calculatedVisibleCount) {
      return null;
    }
    return <components.MultiValue {...props} />;
  };

  const CustomValueContainer = (props: ValueContainerProps) => {
    const { children, getValue } = props;
    const values = getValue();
    const hiddenItemsCount = values.length - calculatedVisibleCount;
    return (
      <components.ValueContainer {...props}>
        {children}
        {hiddenItemsCount > 0 && (
          <OverlayTrigger
            overlay={
              <Tooltip id="overlay-trigger-example">
                <div style={{ padding: '5px' }}>
                  {selected.slice(calculatedVisibleCount).map((item, index) => (
                    <div key={item.ID || index} style={{ padding: '2px 0' }}>
                      {item.Title}
                    </div>
                  ))}
                </div>
              </Tooltip>
            }
          >
            <div
              data-tooltip-id={tooltipId.current}
              style={{
                backgroundColor: '#e6e6e6',
                borderRadius: '2px',
                padding: '0px',
                margin: '0px',
                display: 'flex',
                alignItems: 'center',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                color: '#444'
              }}
            >
              +{hiddenItemsCount}
            </div>
          </OverlayTrigger>
        )}
      </components.ValueContainer>
    );
  };

  const customComponents = {
    ...animatedComponents,
    MultiValue: CustomMultiValue,
    ValueContainer: CustomValueContainer,
    ClearIndicator
  };

  return (
    <div ref={containerRef}>
      <Form.Label>{title}</Form.Label>
      <ReactSelect
        key={dropdownKey}
        components={customComponents}
        closeMenuOnSelect={true}
        isMulti
        options={options}
        value={selected}
        placeholder={title}
        onChange={handleChange}
        styles={{
          multiValue: base => ({
            ...base,
            minWidth: 'auto',
            maxWidth: '100px',
            flexShrink: 0
          }),
          valueContainer: base => ({
            ...base,
            flexWrap: 'nowrap',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            padding: '2px 8px'
          }),
          multiValueLabel: base => ({
            ...base,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100px'
          })
        }}
      />
    </div>
  );
}

export default CategoryDropdown;
